import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { StringFormatter } from '@repo/toolkit/string'
import { SVGProps } from '@repo/toolkit/types'
import Badge, { BadgeProps } from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  NoWalkingPersonIcon,
  PharmacyIcon,
  StarFilledIcon,
  WalkingPersonIcon
} from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { MedicalStatusEnum } from '../../../../lib/types/enums/enrollment/medical-status.enum'
import { EnrollmentCompact } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { LocaleKeys } from '../../../../locale/translation-types'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import InformationToolTip from '../../../elements/information-tooltip'
import { FriendBadge } from '../../trip-dates/trip-dates.ui'

type EnrollmentBadgeKey = 'medicalStatus' | 'isAA' | 'canGoOutAlone'

type EnrollmentBadgeMapType = {
  icon: (props: SVGProps, active?: boolean) => React.ReactNode
  text: string
  variant: BadgeProps['variant']
  iconColor: TailwindColor
  tooltipText: (active?: boolean) => LocaleKeys
}

const EnrollmentBadgeMap: Record<EnrollmentBadgeKey, EnrollmentBadgeMapType> = {
  medicalStatus: {
    icon: (props) => <PharmacyIcon {...props} />,
    text: 'Santé',
    variant: 'success',
    iconColor: tailwindColors.green.DEFAULT,
    tooltipText: () => 'staff.personal.space.students.columns.medical.status'
  },
  canGoOutAlone: {
    icon: (props, enabled) =>
      enabled ? <WalkingPersonIcon {...props} /> : <NoWalkingPersonIcon {...props} />,
    text: 'Peut sortir seul',
    variant: 'grey',
    iconColor: tailwindColors.grey.DEFAULT,
    tooltipText: (active) =>
      active
        ? 'staff.personal.space.students.columns.can.go.out.alone'
        : 'staff.personal.space.students.columns.can.not.go.out.alone'
  },
  isAA: {
    icon: (props) => <StarFilledIcon {...props} />,
    text: 'AA',
    variant: 'yellow',
    iconColor: tailwindColors.yellow.DEFAULT,
    tooltipText: () => 'staff.personal.space.students.columns.aa'
  }
}

type EnrollmentBadgeProps = {
  enrollment: EnrollmentCompact
  keys: EnrollmentBadgeKey[]
  size?: BadgeProps['size']
  t: TFunction<AvailableLanguages, undefined>
}

export function EnrollmentBadges({ enrollment, keys, size, t }: EnrollmentBadgeProps) {
  const display = Object.entries(EnrollmentBadgeMap)
    .filter(([key]) => {
      if (!keys.includes(key as EnrollmentBadgeKey)) return false
      if (key === 'isAA') return enrollment.isAA
      if (key === 'medicalStatus')
        return [
          MedicalStatusEnum.DECLARATION_SIGNED,
          MedicalStatusEnum.ATTESTATION_SIGNED,
          MedicalStatusEnum.VALIDATED_BY_LEC
        ].includes(enrollment.medicalStatus)

      return true
    })
    .map(([key, value]) => (
      <InformationToolTip
        key={key}
        icon={
          <Badge
            size={size}
            variant={value.variant}
            icon={value.icon(
              { color: value.iconColor },
              key === 'canGoOutAlone' && enrollment.canGoOutAlone
            )}
          />
        }
        contentVariant={'default'}
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          {t(value.tooltipText(key === 'canGoOutAlone' && enrollment.canGoOutAlone))}
        </Typography>
      </InformationToolTip>
    ))

  return <div className="flex gap-2">{display}</div>
}

export const affectedStaffEnrollmentsColumns: (
  t: TFunction<AvailableLanguages, undefined>
) => Column<EnrollmentCompact>[] = (t) => [
  {
    accessorKey: 'childLastName',
    enableSorting: true,
    header: t('staff.personal.space.students.columns.last.name'),
    cell: ({ row }) => valueRender(row.original.childLastName, (s) => s.toUpperCase())
  },
  {
    accessorKey: 'childFirstName',
    header: t('staff.personal.space.students.columns.first.name')
  },
  {
    accessorKey: '',
    header: ' ',
    cell: ({ row }) => (
      <EnrollmentBadges
        t={t}
        enrollment={row.original}
        keys={['medicalStatus', 'isAA', 'canGoOutAlone']}
      />
    )
  },
  {
    accessorKey: 'childBirthDate',
    enableSorting: true,
    header: t('staff.personal.space.students.columns.age'),
    cell: ({ row }) => EnrollmentProperties.getAgeAtDeparture(row.original)?.toString() ?? '-'
  },
  {
    accessorKey: 'childGender',
    enableSorting: true,
    header: t('staff.personal.space.students.columns.gender'),
    cell: ({ row }) =>
      valueRender(row.original.childGender, (g) => genderOneLetterMapping[g].label)
  },
  {
    accessorKey: 'childSchoolLevel',
    header: t('staff.personal.space.students.columns.school.level'),
    cell: ({ row }) =>
      valueRender(row.original.childSchoolLevel, (level) => schoolLevelEnumMapping[level])
  },
  {
    accessorKey: 'score',
    header: t('staff.personal.space.students.columns.class.level'),
    cell: ({ row }) => {
      const score = row.original.languageTestSubmission?.score
      if (!score) return '-'
      return (
        <div className="flex flex-row gap-2">
          <InformationToolTip
            icon={
              <div className="flex flex-row gap-2">
                <Typography.Link variant={'secondary-body'}>
                  {valueRender(EnrollmentProperties.getEnrollmentScore(row.original)?.code)}
                </Typography.Link>
                <Badge variant={'danger'} text={`${score}/100`} size="xsmall" />
              </div>
            }
            contentVariant={'default'}
          >
            <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
              Voir le test du jeune
            </Typography>
          </InformationToolTip>
        </div>
      )
    }
  },
  {
    accessorKey: 'mainProposition.journey.date.product.formula.title',
    header: t('staff.personal.space.students.columns.formula'),
    cell: ({ row }) =>
      StringFormatter.shortenText(
        EnrollmentProperties.getEnrollmentFormula(row.original).value ?? '',
        14
      )
  },
  {
    accessorKey: 'mainProposition.journey.mainOption.title',
    header: t('staff.personal.space.students.columns.theme'),
    cell: ({ row }) =>
      StringFormatter.shortenText(
        EnrollmentProperties.getEnrollmentMainOption(row.original).value ?? '',
        14
      )
  },
  {
    accessorKey: 'mainProposition.options.option.title',
    header: t('staff.personal.space.students.columns.optional.option'),
    cell: ({ row }) => valueRender(EnrollmentProperties.getEnrollmentOptions(row.original).value)
  },
  {
    accessorKey: 'mainProposition.mainTrip',
    header: t('staff.personal.space.students.columns.main.trip'),
    cell: ({ row }) => EnrollmentProperties.getEnrollmentMainTripDetails(row.original)
  },
  {
    accessorKey: 'mainProposition.journeyTripPlace.label',
    enableSorting: true,
    header: t('staff.personal.space.students.columns.departure.city'),
    cell: ({ row }) => valueRender(EnrollmentProperties.getEnrollmentTripPlace(row.original).value)
  },
  {
    accessorKey: 'parent1PhoneNumber',
    header: t('staff.personal.space.students.columns.parent1.phone.number'),
    cell: ({ row }) => (
      <div className="flex flex-col gap-2 py-2">
        <Typography variant={'secondary-body'}>
          {valueRender(row.original.parent1PhoneNumber, (ph) => formatPhoneNumber(ph))}
        </Typography>

        {EnrollmentProperties.getParentsByPriority(row.original).parent1 === '1' && (
          <Badge variant={'warning'} className={'h-fit py-1'} text={'Contact prioritaire'} />
        )}
      </div>
    )
  },
  {
    accessorKey: 'parent2PhoneNumber',
    header: t('staff.personal.space.students.columns.parent2.phone.number'),
    cell: ({ row }) => (
      <div className="flex flex-col gap-2 py-2">
        <Typography variant={'secondary-body'}>
          {valueRender(row.original.parent2PhoneNumber, (ph) => formatPhoneNumber(ph))}
        </Typography>

        {EnrollmentProperties.getParentsByPriority(row.original).parent2 === '1' && (
          <Badge variant={'warning'} className={'h-fit py-1'} text={'Contact prioritaire'} />
        )}
      </div>
    )
  },
  {
    accessorKey: 'childPhoneNumber',
    header: t('staff.personal.space.students.columns.student.phone.number'),
    cell: ({ row }) => valueRender(row.original.childPhoneNumber, (ph) => formatPhoneNumber(ph))
  },
  {
    accessorKey: 'id',
    header: t('staff.personal.space.students.columns.friends'),
    cell: ({ row }) => {
      const friends = row.original.declaredFriends
      if (!friends?.length) return '-'
      return (
        <div className="flex flex-col py-2">
          {friends.map((friend) => (
            <FriendBadge key={friend.id} {...friend} variant="transparent" />
          ))}
        </div>
      )
    }
  }
]
export type TravelExpenseTotal = {
  currency?: CurrencyEnum | null
  amount?: number | null
}
